import type { PlayerStats } from '@rw/shared';
import { Link } from 'react-router';
import { Avatar, Card, LevelBadge, Stat, XpBar } from '../../components/ui';
import { formatDistance, formatNumber } from '../../lib/format';
import { MY_COLOR } from '../../lib/colors';
import { useT } from '../../i18n';
import { BadgesCard } from '../achievements/BadgesCard';
import { RankChip } from '../achievements/RankChip';

export function PlayerStatsView({ stats, isMe }: { stats: PlayerStats; isMe?: boolean }) {
  const t = useT();
  const d = formatDistance(stats.totalDistanceM);
  return (
    <>
      <div className="flex flex-col items-center text-center">
        <Avatar name={stats.user.displayName} color={isMe ? MY_COLOR : stats.user.color} size="lg" src={stats.user.avatarUrl} />
        <h1 className="mt-3 font-display text-3xl font-bold" data-testid="profile-name">
          {stats.user.displayName}
        </h1>
        {stats.bio ? (
          <p className="mt-2 max-w-xs whitespace-pre-line text-sm text-ink-300" data-testid="profile-bio">
            {stats.bio}
          </p>
        ) : null}
        <div className="mt-1 flex items-center gap-2 text-sm text-ink-400">
          <LevelBadge level={stats.progress.level} size="sm" />
          <span>{t('Level {n}', { n: stats.progress.level })}</span>
          <RankChip level={stats.progress.level} />
          {stats.rank.xp ? <span>· {t('Rank #{n}', { n: stats.rank.xp })}</span> : null}
        </div>
        {stats.awards.length ? (
          <div className="mt-3 flex flex-wrap justify-center gap-2" data-testid="profile-awards">
            {stats.awards.map((a) => (
              <Link key={`${a.eventId}-${a.scope}`} to={`/events/${a.eventId}`} className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
                {['🥇', '🥈', '🥉'][a.rank - 1]} {a.eventName}
                {a.teamName ? ` · ${a.teamName}` : ''}
              </Link>
            ))}
          </div>
        ) : null}
        {stats.club ? (
          <Link to={`/clubs/${stats.club.id}`} className="mt-2 inline-flex items-center gap-2 rounded-full bg-ink-850 px-3 py-1 text-sm" data-testid="profile-club">
            <span className="hex-clip size-4" style={{ background: stats.club.color }} />
            {stats.club.name}
          </Link>
        ) : null}
      </div>
      <Card className="mt-5">
        <div className="flex justify-between text-sm">
          <span className="font-semibold">{formatNumber(stats.progress.totalXp)} XP</span>
          <span className="text-ink-400 tabular">
            {formatNumber(stats.progress.xpIntoLevel)} / {formatNumber(stats.progress.xpForLevel)}
          </span>
        </div>
        <XpBar into={stats.progress.xpIntoLevel} total={stats.progress.xpForLevel} className="mt-2" />
      </Card>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <Stat label={t('Distance')} value={d.value} unit={d.unit} />
        <Link to={`/players/${stats.user.id}/runs`} className="relative block active:opacity-80" data-testid="runs-stat" aria-label={t('See all runs')}>
          <Stat label={t('Runs')} value={stats.validRuns} unit={stats.totalRuns !== stats.validRuns ? `/ ${stats.totalRuns}` : undefined} />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-ink-500">›</span>
        </Link>
        <Stat label={t('Territories')} value={stats.territoriesOwned} accent="volt" testId="profile-territories" />
        <Stat label={t('Streak')} value={stats.currentStreak} unit={t('best {n}', { n: stats.bestStreak })} accent="sky" />
        <Stat label={t('Distance rank')} value={stats.rank.distance ? `#${stats.rank.distance}` : '—'} accent="gold" />
        <Stat label={t('Territory rank')} value={stats.rank.territory ? `#${stats.rank.territory}` : '—'} accent="gold" />
      </div>
      {stats.territoriesByRegion?.length > 1 ? (
        <p className="mt-2 text-center text-xs text-ink-400" data-testid="profile-territories-by-city">
          {stats.territoriesByRegion.map((r) => `${r.name} ${r.count}`).join(' · ')}
        </p>
      ) : null}
      <BadgesCard userId={stats.user.id} isMe={!!isMe} />
    </>
  );
}
